// filepath: apps/web/src/operator/OperatorAuditPlaceholder.tsx
//
// FR-UI-OPS-2 AC-6 / FR-UI-OPS-3 — Audit section placeholder.
//
// Renders an explicit, localized "not yet implemented" message in a
// live region. The placeholder:
//   - issues ZERO network calls (no fetch, no query hook, no MSW
//     handler) — AC-6 is assertable as "no audit endpoint exists or
//     is contacted";
//   - is axe-core-clean (a single heading + a polite live region that
//     conveys status to assistive tech without asserting focus);
//   - uses only static i18n chrome (INV-9 — no tenant text, no
//     `dangerouslySetInnerHTML`).
//
// FR-UI-OPS-3 redesign: chrome now wraps the placeholder message in a
// `Card` primitive from the design system. The visible test ids and
// i18n keys are PRESERVED verbatim (operatorI18nKeys contract).

import React from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { Card } from "@/design-system";

/**
 * OperatorAuditPlaceholder — the Audit section. Renders a localized
 * "not yet implemented" message. No data fetching of any kind.
 */
export function OperatorAuditPlaceholder(): React.ReactElement {
  const { t } = useTranslation();
  return (
    <section
      data-testid="operator-audit-placeholder"
      className="operator-audit-placeholder"
      aria-labelledby="operator-audit-title"
    >
      <h2
        id="operator-audit-title"
        style={{
          fontSize: "var(--text-2xl)",
          fontWeight: "var(--font-extrabold)",
          color: "var(--text-primary)",
          margin: "0 0 var(--space-4) 0",
        }}
      >
        {t("operator.audit.title", "Audit")}
      </h2>
      <Card padding="lg" data-testid="operator-audit-card">
        <p
          role="status"
          aria-live="polite"
          data-testid="operator-audit-message"
          className="operator-audit-message"
          style={{
            margin: 0,
            fontSize: "var(--text-base)",
            color: "var(--text-body)",
          }}
        >
          {t("operator.audit.placeholder", "Audit log is not yet implemented.")}
        </p>
      </Card>
    </section>
  );
}
